import { base64ListRequest } from "@/services/backend/_lib/base64-list-request";
import { backendApi } from "@/services/backend/api";
import {
  CreateGfsCalculationFromAssessmentRequest,
  CreateGfsCalculationFromOldRequest,
  CreateGfsCalculationRequest,
  DeleteGfsCalculationRequest,
  GfsCalculation,
  GfsCalculationList,
  ListGfsCalculationsRequest,
  LoadGfsCalculationAssessmentRequest,
  PutGfsCalculationRequest,
  SetGfsCalculationImmutableRequest,
} from "@/services/backend/gfs/calculation/types";

const gfsCalculationApi = backendApi
  .enhanceEndpoints({ addTagTypes: ["GfsCalculation", "GfsCalculations"] })
  .injectEndpoints({
    endpoints: (build) => ({
      gfsCalculationCreate: build.mutation<
        GfsCalculation,
        CreateGfsCalculationRequest
      >({
        query: (body) => ({ url: "/gfs/calculations", method: "POST", body }),
        invalidatesTags: [{ type: "GfsCalculations", id: "LIST" }],
      }),
      gfsCalculationCreateFromOld: build.mutation<
        GfsCalculation,
        CreateGfsCalculationFromOldRequest
      >({
        query: (body) => ({
          url: "/gfs/calculations/create-from-old",
          method: "POST",
          body,
        }),
        invalidatesTags: [{ type: "GfsCalculations", id: "LIST" }],
      }),
      gfsCalculationCreateFromAssessment: build.mutation<
        GfsCalculation,
        CreateGfsCalculationFromAssessmentRequest
      >({
        query: (body) => ({
          url: "/gfs/calculations/create-from-assessment",
          method: "POST",
          body,
        }),
        invalidatesTags: [{ type: "GfsCalculations", id: "LIST" }],
      }),
      gfsCalculationLoadAssessment: build.mutation<
        GfsCalculation,
        LoadGfsCalculationAssessmentRequest
      >({
        query: (body) => ({
          url: "/gfs/calculations/load-assessment",
          method: "POST",
          body,
        }),
        async onQueryStarted(request, { dispatch, queryFulfilled }) {
          try {
            const { data } = await queryFulfilled;
            dispatch(
              gfsCalculationApi.util.updateQueryData(
                "gfsCalculationGet",
                { id: request.id },
                (draft) => Object.assign(draft, data),
              ),
            );
          } catch {
            return;
          }
        },
        invalidatesTags: (_result, error) =>
          error ? [] : [{ type: "GfsCalculations", id: "LIST" }],
      }),
      gfsCalculationPut: build.mutation<
        GfsCalculation,
        PutGfsCalculationRequest
      >({
        query: (body) => ({ url: "/gfs/calculations", method: "PUT", body }),
        async onQueryStarted(request, { dispatch, queryFulfilled }) {
          try {
            const { data } = await queryFulfilled;
            dispatch(
              gfsCalculationApi.util.updateQueryData(
                "gfsCalculationGet",
                { id: request.id },
                (draft) => Object.assign(draft, data),
              ),
            );
          } catch {
            return;
          }
        },
        invalidatesTags: (_result, error, request) =>
          error
            ? [{ type: "GfsCalculation", id: request.id }]
            : [{ type: "GfsCalculations", id: "LIST" }],
      }),
      gfsCalculationSetImmutable: build.mutation<
        GfsCalculation,
        SetGfsCalculationImmutableRequest
      >({
        query: (body) => ({
          url: "/gfs/calculations/set-immutable",
          method: "POST",
          body,
        }),
        invalidatesTags: (_result, _error, request) => [
          { type: "GfsCalculation", id: request.id },
          { type: "GfsCalculations", id: "LIST" },
        ],
      }),
      gfsCalculationDelete: build.mutation<void, DeleteGfsCalculationRequest>({
        query: (body) => ({
          url: "/gfs/calculations",
          method: "DELETE",
          body,
        }),
        invalidatesTags: (_result, _error, request) => [
          { type: "GfsCalculation", id: request.id },
          { type: "GfsCalculations", id: "LIST" },
        ],
      }),
      gfsCalculationGet: build.query<GfsCalculation, { id: string }>({
        query: (params) => ({
          url: "/gfs/calculations/get",
          method: "GET",
          params,
        }),
        providesTags: (_result, _error, request) => [
          { type: "GfsCalculation", id: request.id },
        ],
      }),
      gfsCalculationList: build.query<
        GfsCalculationList,
        ListGfsCalculationsRequest
      >({
        query: (params) => ({
          url: "/gfs/calculations/list",
          method: "GET",
          params: base64ListRequest(params),
        }),
        providesTags: [{ type: "GfsCalculations", id: "LIST" }],
      }),
    }),
  });

export const {
  useGfsCalculationCreateMutation,
  useGfsCalculationCreateFromAssessmentMutation,
  useGfsCalculationCreateFromOldMutation,
  useGfsCalculationLoadAssessmentMutation,
  useGfsCalculationPutMutation,
  useGfsCalculationSetImmutableMutation,
  useGfsCalculationDeleteMutation,
  useGfsCalculationGetQuery,
  useGfsCalculationListQuery,
} = gfsCalculationApi;

/**
 * The editor needs to update the cached calculation as soon as the user changes a value.
 * This function keeps that RTK Query detail next to the calculation endpoint.
 * It lets the editor pass a complete calculation to the shared debounce helper without changing debounce.ts.
 */
export function updateGfsCalculationCache(calculation: GfsCalculation) {
  return gfsCalculationApi.util.updateQueryData(
    "gfsCalculationGet",
    { id: calculation.id },
    (draft) => Object.assign(draft, calculation),
  );
}
